import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { ORDER_URL, RESERVE_URL } from "@/data/menu";

const LINKS = [
  { href: "#menu", label: "Menu", testid: "nav-menu-link" },
  { href: "#hours", label: "Hours", testid: "nav-hours-link" },
  { href: "#visit", label: "Visit", testid: "nav-visit-link" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-[#FFF9F1]/85 backdrop-blur-xl border-b border-[#3A1F14]/10 shadow-[0_8px_30px_-18px_rgba(24,44,32,0.35)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <a href="#top" data-testid="nav-logo" className="group flex items-center gap-3">
          <span className="block h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-[#3A1F14]/10 bg-white shadow-sm transition-transform duration-500 group-hover:scale-105 group-hover:rotate-2">
            <img
              src="/logo.jpg"
              alt="Northgate Flavour Express logo"
              className="h-full w-full scale-[1.45] object-cover"
            />
          </span>
          <span className={`hidden font-display text-xl font-semibold leading-none transition-colors duration-500 sm:block ${scrolled ? "text-forest" : "text-[#FFF9F1]"}`}>
            Northgate <span className={`italic font-medium ${scrolled ? "text-[#E32219]" : "text-[#FFC61A]"}`}>Flavour</span> Express
          </span>
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className={`font-mono-alt text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                scrolled ? "text-muted-ink hover:text-terracotta" : "text-[#FFF9F1]/75 hover:text-[#FFC61A]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={RESERVE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-reserve-btn"
            className={`rounded-full border px-5 py-2 text-sm font-medium transition-all duration-300 ${
              scrolled
                ? "border-forest/25 text-forest hover:border-forest hover:bg-forest hover:text-[#FFF9F1]"
                : "border-[#FFF9F1]/40 text-[#FFF9F1] hover:bg-[#FFF9F1] hover:text-[#2B170D]"
            }`}
          >
            Reserve a Table
          </a>
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-order-online-btn"
            className="rounded-full bg-terracotta px-5 py-2 text-sm font-semibold text-[#FFF9F1] transition-all duration-300 hover:bg-[#B8170F] hover:shadow-[0_10px_24px_-10px_rgba(227,34,25,0.7)]"
          >
            Order Online
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`grid h-10 w-10 place-items-center rounded-full border transition-colors duration-300 lg:hidden ${
            scrolled ? "border-forest/20 text-forest" : "border-[#FFF9F1]/30 text-[#FFF9F1]"
          }`}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <MenuIcon size={18} />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-[#3A1F14]/10 bg-[#FFF9F1]/95 px-5 pb-6 pt-4 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`${l.testid}-mobile`}
                className="font-mono-alt text-xs uppercase tracking-[0.25em] text-muted-ink"
              >
                {l.label}
              </a>
            ))}
            <div className="mt-2 flex gap-3">
              <a
                href={ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-order-online-btn-mobile"
                className="flex-1 rounded-full bg-terracotta px-5 py-2.5 text-center text-sm font-semibold text-[#FFF9F1]"
              >
                Order Online
              </a>
              <a
                href={RESERVE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-reserve-btn-mobile"
                className="flex-1 rounded-full border border-forest/25 px-5 py-2.5 text-center text-sm font-medium text-forest"
              >
                Reserve
              </a>
            </div>
          </div>
        </div>
      )}
    </motion.header>
  );
};
